import { baseApi } from 'src/Services/api.ts'

export interface Tool {
  id: number
  name: string
  status: 'ACTIVE' | 'ARCHIVED'
  condition?: 'GOOD' | 'SATISFACTORY'
  imageUrl?: string
  description?: string
  toolWorkPrice?: number
  images?: ToolImage[]
  modelTools?: {
    id: number
    modelId: number
    toolId: number
    model: {
      id: number
      name: string
      type: string
    }
  }[]
  toolModels?: {
    id: number
    name: string
    type: string
  }[]
}

export interface ToolImage {
  id: number
  toolId: number
  url: string
}

export interface ToolsQueryParams {
  page?: number
  limit?: number
  name?: string
}

export interface PaginationMeta {
  pageNumber: number
  pageSize: number
  itemCount: number
  pageCount: number
  hasPreviousPage: boolean
  hasNextPage: boolean
}

export interface ToolsResponse {
  data: Tool[]
  meta: PaginationMeta
}

export interface AddToolBody {
  name: string
  toolWorkPrice: number
  condition?: 'GOOD' | 'SATISFACTORY'
  description?: string
}

export interface UpdateToolBody {
  name: string
  toolWorkPrice: number
  condition?: 'GOOD' | 'SATISFACTORY'
  description?: string
}

export const toolsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllTools: builder.query<ToolsResponse, ToolsQueryParams | void>({
      query: (params) => {
        const queryParams = new URLSearchParams()

        queryParams.append('limit', '20')

        if (params) {
          if (params.page) queryParams.append('page', params.page.toString())
          if (params.name) queryParams.append('name', params.name)
        }

        const queryString = queryParams.toString()
        return `/tools${queryString ? `?${queryString}` : ''}`
      },
      serializeQueryArgs: ({ endpointName }) => endpointName,

      merge: (currentCache, newItems, meta) => {
        if (!meta?.arg?.page || meta?.arg?.page === 1) {
          return {
            data: newItems?.data,
            meta: newItems.meta,
          }
        }
        return {
          data: [...(currentCache?.data || []), ...(newItems?.data || [])],
          meta: newItems.meta,
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg)
      },
      providesTags: ['tools'],
    }),

    addTool: builder.mutation<void, AddToolBody>({
      query: (data) => ({
        url: `/tools`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['tools'],
    }),

    associateToolWithModel: builder.mutation<
      void,
      { modelId: number; toolId: number }
    >({
      query: ({ modelId, toolId }) => ({
        url: `/models/${modelId}/tools/${toolId}`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['tools', 'models', 'transport'],
    }),

    dissociateToolFromModel: builder.mutation<
      void,
      { modelId: number; toolId: number }
    >({
      query: ({ modelId, toolId }) => ({
        url: `/models/${modelId}/tools/${toolId}`,
        method: 'DELETE',
        body: {},
      }),
      invalidatesTags: ['tools', 'models', 'transport'],
    }),

    addToolImages: builder.mutation<
      ToolImage[],
      { toolId: number; images: File[] }
    >({
      query: ({ toolId, images }) => {
        const formData = new FormData()
        images.forEach((image) => formData.append('images', image))
        return {
          url: `/tools/${toolId}/images`,
          method: 'POST',
          body: formData,
        }
      },
      invalidatesTags: ['tools', 'models'],
    }),

    deleteToolImage: builder.mutation<
      void,
      { toolId: number; imageId: number }
    >({
      query: ({ toolId, imageId }) => ({
        url: `/tools/${toolId}/images/${imageId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['tools', 'models'],
    }),

    deleteTool: builder.mutation<void, number>({
      query: (toolId) => ({
        url: `/tools/${toolId}/delete`,
        method: 'POST',
        body: {},
      }),
      invalidatesTags: ['tools', 'models', 'transport'],
    }),

    updateTool: builder.mutation<void, { id: number; data: UpdateToolBody }>({
      query: ({ id, data }) => ({
        url: `/tools/${id}`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['tools', 'models', 'transport'],
    }),
  }),

  overrideExisting: true,
})

export const {
  useGetAllToolsQuery,
  useAddToolMutation,
  useAssociateToolWithModelMutation,
  useDissociateToolFromModelMutation,
  useDeleteToolMutation,
  useUpdateToolMutation,
  useAddToolImagesMutation,
  useDeleteToolImageMutation,
} = toolsApi
